import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MailCheck, LifeBuoy, Send } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { LanguageSwitcher } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const BUSINESS_CATS = ["artesanos_mantenimiento", "automotive", "hogar_persona", "enogastronomia",
  "excursiones_actividades", "bienestar", "entretenimiento", "deporte", "restauracion",
  "movilidad", "coffee_drinks"];

export default function Auth() {
  const { t, lang } = useI18n();
  const { login, register, resendVerification, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", plan: "free",
    is_business: false, business_name: "", business_address: "", business_nif: "", business_category: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");     // registered, awaiting verify
  const [needsVerify, setNeedsVerify] = useState(false);    // login blocked by verification
  const [forgotSent, setForgotSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [supportOpen, setSupportOpen] = useState(false);
  const [supportSending, setSupportSending] = useState(false);
  const [sForm, setSForm] = useState({ name: "", email: "", subject: "", message: "" });
  const sSet = (k, v) => setSForm((f) => ({ ...f, [k]: v }));
  const sendSupport = async () => {
    if (!sForm.name || !sForm.email || !sForm.subject || !sForm.message) { toast.error(t("common.required")); return; }
    setSupportSending(true);
    try {
      await api.post("/support/public", sForm);
      toast.success(t("auth.supportSent"));
      setSupportOpen(false);
      setSForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSupportSending(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNeedsVerify(false); setLoading(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(form.email);
        setForgotSent(true);
      } else if (mode === "login") {
        await login(form.email, form.password);
        toast.success(t("auth.loggedIn"));
        navigate("/");
      } else {
        const data = await register({ ...form, is_business: form.plan === "unlimited", language: lang });
        if (data.requires_verification) {
          setPendingEmail(data.email);
        } else {
          navigate("/onboarding");
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      if (status === 403 && /email|confirm|verif/i.test(detail)) { setNeedsVerify(true); setPendingEmail(form.email); }
      setError(detail);
    } finally { setLoading(false); }
  };

  const resend = async (email) => {
    try { await resendVerification(email); toast.success(t("auth.resendSent")); }
    catch { toast.error("Error"); }
  };

  // ---- Confirmation screen (after registration or blocked login) ----
  if (pendingEmail && (mode === "register" || needsVerify)) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 bg-gradient-to-br from-[#F8FAF9] to-teal-50">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-xl p-8 text-center cm-fade-up" data-testid="verify-notice">
          <span className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-teal-50 text-[#008080] mb-4"><MailCheck className="h-8 w-8" /></span>
          <h1 className="font-display font-bold text-2xl">{t("auth.checkEmailTitle")}</h1>
          <p className="text-sm text-slate-500 mt-2">{t("auth.checkEmailDesc")}</p>
          <p className="font-semibold text-slate-800 mt-1">{pendingEmail}</p>
          <div className="mt-4 p-3 rounded-xl bg-amber-50 text-amber-800 text-xs">{t("auth.spamNote")}</div>
          <Button data-testid="resend-verify-btn" onClick={() => resend(pendingEmail)} variant="outline" className="w-full mt-5">{t("auth.resend")}</Button>
          <button onClick={() => { setPendingEmail(""); setNeedsVerify(false); setMode("login"); setError(""); }} className="text-sm text-[#008080] font-semibold mt-4">{t("auth.backToLogin")}</button>
        </div>
      </div>
    );
  }

  // ---- Forgot-password confirmation screen ----
  if (mode === "forgot" && forgotSent) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 bg-gradient-to-br from-[#F8FAF9] to-teal-50">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-xl p-8 text-center cm-fade-up" data-testid="forgot-sent">
          <span className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-teal-50 text-[#008080] mb-4"><MailCheck className="h-8 w-8" /></span>
          <h1 className="font-display font-bold text-2xl">{t("auth.forgotSent")}</h1>
          <p className="text-sm text-slate-500 mt-2">{t("auth.forgotSentDesc")}</p>
          <div className="mt-4 p-3 rounded-xl bg-amber-50 text-amber-800 text-xs">{t("auth.spamNote")}</div>
          <button onClick={() => { setMode("login"); setForgotSent(false); setError(""); }} className="text-sm text-[#008080] font-semibold mt-5">{t("auth.backToLogin")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2 relative">
      <div className="absolute top-4 right-4 z-20" data-testid="auth-lang-switcher"><LanguageSwitcher /></div>
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1561098556-24b58cc7993b?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="Canarias" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2537]/90 to-[#008080]/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-display font-extrabold text-4xl leading-tight">Compra. Vende.<br />Intercambia.<br /><span className="text-[#FF9F1C]">Cerca de ti.</span></h2>
          <p className="mt-3 text-teal-100">El marketplace local de las Islas Canarias.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center justify-center mb-8">
            <img src="/logo.png" alt="CANARIAS MARKET" className="h-20 w-auto object-contain" />
          </Link>
          <h1 className="font-display font-bold text-2xl text-center">{mode === "forgot" ? t("auth.forgotTitle") : mode === "login" ? t("auth.welcome") : t("auth.welcomeNew")}</h1>
          <p className="text-center text-sm text-slate-500 mt-1 mb-6">{mode === "forgot" ? t("auth.forgotDesc") : t("auth.loginToContinue")}</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label>{lang === "it" ? "Scegli il tuo piano" : lang === "en" ? "Choose your plan" : "Elige tu plan"}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { id: "free", name: "Free", price: lang === "it" ? "0€ · 3 annunci" : lang === "en" ? "0€ · 3 listings" : "0€ · 3 anuncios" },
                      { id: "start", name: "Start", price: "9,90€ · 50" },
                      { id: "pro", name: "Pro", price: "19,90€ · 150" },
                      { id: "unlimited", name: "Unlimited", price: lang === "it" ? "99,90€ · illim." : lang === "en" ? "99,90€ · unltd" : "99,90€ · ilim." },
                    ].map((p) => (
                      <button key={p.id} type="button" data-testid={`reg-plan-${p.id}`} onClick={() => set("plan", p.id)}
                        className={`h-14 rounded-xl text-left px-3 border transition-colors ${form.plan === p.id ? "bg-[#008080] text-white border-[#008080]" : "bg-white text-slate-600 border-slate-200"}`}>
                        <span className="block text-sm font-semibold">{p.name}</span>
                        <span className={`block text-[11px] ${form.plan === p.id ? "text-teal-100" : "text-slate-400"}`}>{p.price}</span>
                      </button>
                    ))}
                  </div>
                  {form.plan !== "free" && (
                    <p className="text-[11px] text-amber-600 mt-1.5">{lang === "it" ? "Dopo aver verificato l'email, accedi per completare il pagamento (carta o Bizum)." : lang === "en" ? "After verifying your email, log in to complete payment (card or Bizum)." : "Tras verificar tu email, inicia sesión para completar el pago (tarjeta o Bizum)."}</p>
                  )}
                </div>
                {form.plan === "unlimited" && (
                  <div className="space-y-3 rounded-2xl border border-teal-100 bg-teal-50/50 p-3">
                    <div><Label>{t("monetization.business.fName")}</Label>
                      <Input data-testid="reg-biz-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="mt-1 bg-white" /></div>
                    <div><Label>{t("monetization.business.fAddress")}</Label>
                      <Input data-testid="reg-biz-address" value={form.business_address} onChange={(e) => set("business_address", e.target.value)} className="mt-1 bg-white" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label>{t("monetization.business.fNif")}</Label>
                        <Input data-testid="reg-biz-nif" value={form.business_nif} onChange={(e) => set("business_nif", e.target.value)} className="mt-1 bg-white" /></div>
                      <div><Label>{t("monetization.business.fCategory")}</Label>
                        <Select value={form.business_category} onValueChange={(v) => set("business_category", v)}>
                          <SelectTrigger data-testid="reg-biz-category" className="mt-1 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>{BUSINESS_CATS.map((c) => <SelectItem key={c} value={c}>{t(`monetization.business.cats.${c}`)}</SelectItem>)}</SelectContent>
                        </Select></div>
                    </div>
                    <p className="text-xs text-teal-700">{t("auth.businessRegNote")}</p>
                  </div>
                )}
              </>
            )}
            <div><Label>{t("auth.email")}</Label>
              <Input data-testid="auth-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1" /></div>
            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t("auth.password")}</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }} className="text-xs text-[#008080] font-medium">{t("auth.forgotLink")}</button>
                  )}
                </div>
                <Input data-testid="auth-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required className="mt-1" />
              </div>
            )}
            {error && <p data-testid="auth-error" className="text-sm text-rose-600">{error}</p>}
            <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">
              {loading ? t("common.loading") : mode === "forgot" ? t("auth.forgotCta") : mode === "login" ? t("auth.loginCta") : t("auth.registerCta")}
            </Button>
          </form>

          {mode === "forgot" ? (
            <p className="text-center text-sm text-slate-500 mt-5">
              <button data-testid="forgot-back-login" onClick={() => { setMode("login"); setError(""); }} className="text-[#008080] font-semibold">{t("auth.backToLogin")}</button>
            </p>
          ) : (
            <p className="text-center text-sm text-slate-500 mt-5">
              {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
              <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNeedsVerify(false); }} className="text-[#008080] font-semibold">
                {mode === "login" ? t("auth.register") : t("auth.login")}
              </button>
            </p>
          )}

          {mode !== "forgot" && (
            <div className="mt-4 pt-4 border-t border-slate-100 text-center">
              <button type="button" data-testid="contact-support-link" onClick={() => setSupportOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#008080] hover:text-[#006666] transition-colors">
                <LifeBuoy className="h-4 w-4" /> {t("auth.contactSupport")}
              </button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="max-w-md" data-testid="support-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-[#008080]" />{t("auth.supportTitle")}</DialogTitle>
            <DialogDescription>{t("auth.supportDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("auth.supportName")} *</Label><Input data-testid="support-name" value={sForm.name} onChange={(e) => sSet("name", e.target.value)} className="mt-1" /></div>
              <div><Label>{t("auth.email")} *</Label><Input data-testid="support-email" type="email" value={sForm.email} onChange={(e) => sSet("email", e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>{t("auth.supportSubject")} *</Label><Input data-testid="support-subject" value={sForm.subject} onChange={(e) => sSet("subject", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("auth.supportMessage")} *</Label><Textarea data-testid="support-message" rows={4} value={sForm.message} onChange={(e) => sSet("message", e.target.value)} className="mt-1" placeholder={t("auth.supportPlaceholder")} /></div>
          </div>
          <Button data-testid="support-send-btn" onClick={sendSupport} disabled={supportSending} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">
            {supportSending ? t("common.loading") : <><Send className="h-4 w-4 mr-1" />{t("auth.supportSend")}</>}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
